<script setup>
import { $api } from '@/services';
import {
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
	computed,
	defineEmits,
} from 'vue';
import { useRoute } from 'vue-router';
import { _GET_DATE_2 } from '@/helper/formatDate';
import Loading from '@/components/common/Loading';
import Popper from 'vue3-popper';
import { useStore } from 'vuex';
import Multiselect from '@suadelabs/vue3-multiselect';
import GoalDetail from '@/views/goal/GoalDetail.vue';
import TooltipGoalDetail from '@/components/elements/tooltipGoalDetail/TooltipGoalDetail';

const emit = defineEmits(['updateResult']);
const route = useRoute();
const loading = ref(false);
const empty = ref(false);
const data = ref([]);
const statistics = ref([]);
const result = ref([]);
const emotion_cooperation_result_by_day = ref([]);
const startDate = ref(route.query.start_date);
const endDate = ref(route.query.end_date);
const chartOptions = ref({
	dataLabels: {
		enabled: false,
	},
	colors: ['#7AD2C6', '#93BF56', '#F8BB54', '#F47C7C', '#E5E7EB'],
	labels: [
		'Đạt 80 - 100%',
		'Đạt 55 - 75%',
		'Đạt 30 - 50%',
		'Đạt 5 - 25%',
		'X',
	],
	legend: {
		show: false,
	},
	tooltip: {
		fillSeriesColor: true,
		y: {
			formatter: function (value) {
				return value + ' mục tiêu';
			},
		},
	},
	stroke: {
		lineCap: 'round',
	},
});
const chartOptions3 = ref({
	colors: ['#9EDF93', '#D0DB54', '#FFE68C', '#FF9D6D', '#FF6967'],
	chart: {
		width: 380,
		type: 'polarArea',
	},
	labels: ['Vui vẻ', 'Bình thường', 'Khó chịu', 'Tức giận', 'Bùng nổ'],
	fill: {
		opacity: 1,
		colors: ['#9EDF93', '#D0DB54', '#FFE68C', '#FF9D6D', '#FF6967'],
	},
	stroke: {
		lineCap: 'round',
	},
	yaxis: {
		show: false,
	},
	tooltip: {
		fillSeriesColor: true,
		y: {
			formatter: function (value) {
				if (!value || value == 'null') {
					return 'Không thực hiện';
				}
				return value + '%';
			},
		},
	},
	legend: {
		show: false,
	},
	dataLabels: {
		enabled: false,
	},
	plotOptions: {
		polarArea: {
			rings: {
				strokeWidth: 0,
			},
			spokes: {
				strokeWidth: 0,
			},
		},
	},
});
const mixChartOptions = ref({
	chart: {
		type: 'line',
		stacked: false,
		animations: {
			enabled: false,
			dynamicAnimation: {
				enabled: false,
			},
		},
		redrawOnParentResize: false,
	},
	dataLabels: {
		enabled: true,
		enabledOnSeries: [0, 1],
		offsetY: -10,
		background: {
			enabled: true,
			foreColor: '#334155',
		},
		formatter: function (val) {
			return val + '%';
		},
	},
	stroke: {
		width: [0.5, 0.5, 4],
	},
	yaxis: [
		{
			opposite: false,
			axisTicks: {
				show: false,
			},
			axisBorder: {
				show: false,
			},
			labels: {
				show: false,
				formatter: function (value) {
					if (!value || value == 'null') {
						return 'Không thực hiện';
					}
					return value + '%';
				},
			},
			title: {
				show: false,
			},
			max: 100,
		},
		{
			opposite: false,
			axisTicks: {
				show: false,
			},
			axisBorder: {
				show: false,
			},
			labels: {
				show: false,
				formatter: function (value) {
					if (!value || value == 'null') {
						return 'Không thực hiện';
					}
					return value + '%';
				},
			},
			title: {
				show: false,
			},
			max: 100,
		},
		{
			opposite: false,
			axisTicks: {
				show: false,
			},
			axisBorder: {
				show: false,
			},
			labels: {
				show: false,
				formatter: function (value) {
					if (!value || value == 'null') {
						return 'Không thực hiện';
					}
					return value + '%';
				},
			},
			title: {
				show: false,
			},
			max: 100,
		},
	],
	tooltip: {
		fixed: {
			enabled: true,
			position: 'topLeft', // topRight, topLeft, bottomRight, bottomLeft
			offsetY: 30,
			offsetX: 60,
		},
	},
	legend: {
		horizontalAlign: 'left',
		offsetX: 40,
	},
	plotOptions: {
		bar: {
			horizontal: false,
			columnWidth: '20%',
			borderRadius: 4,
		},
	},
	labels: [],
	colors: ['#FFD270', '#DA8F8F', '#6ED0B9'],
	markers: {
		size: 8,
		strokeColors: '#fff',
		strokeWidth: 2,
	},
});
const emotion_cooperation_result_by_day_data = ref([
	{
		name: 'Cảm xúc',
		type: 'column',
		data: [0, 0, 0, 0, 0],
	},
	{
		name: 'Sự tham gia',
		type: 'column',
		data: [0, 0, 0, 0, 0],
	},
	{
		name: 'Kết quả buổi học',
		type: 'line',
		data: [0, 0, 0, 0, 0],
	},
]);
const emotion_cooperation_result_by_day_emotion = ref([]);
const emotion_cooperation_result_by_day_rating = ref([]);
const emotion_cooperation_result_by_day_result = ref([]);
const emotion_cooperation_result_by_day_labels = ref([]);
const mixChart = ref(null);
const mixChartFeeding = ref(false);
let mixFeedToken = 0;
let mixFeedIdle = 0;

const cancelMixFeed = () => {
	mixFeedToken += 1;
	mixChartFeeding.value = false;
	if (typeof window.cancelIdleCallback === 'function') {
		window.cancelIdleCallback(mixFeedIdle);
	}
	clearTimeout(mixFeedIdle);
};

const scheduleMixFeed = (run) => {
	if (typeof window.requestIdleCallback === 'function') {
		mixFeedIdle = window.requestIdleCallback(run, { timeout: 320 });
		return;
	}
	mixFeedIdle = setTimeout(run, 32);
};

const mixColumnWidth = (length) => {
	const optimal = 5 + 15 / (1 + 30 * Math.exp(-length / 3));
	return `${optimal}%`;
};

const toMixSeries = (ratings, emotions, results) => [
	{
		name: 'Sự tham gia',
		type: 'column',
		data: ratings,
	},
	{
		name: 'Cảm xúc',
		type: 'column',
		data: emotions,
	},
	{
		name: 'Kết quả buổi học',
		type: 'line',
		data: results,
	},
];

const feedMixChart = (items) => {
	cancelMixFeed();
	const token = mixFeedToken;
	const list = Array.isArray(items) ? items : [];
	const ratings = [];
	const emotions = [];
	const results = [];
	const labels = [];
	let index = 0;
	const chunk = 30;

	const paint = (done) => {
		const ratingData = ratings.slice();
		const emotionData = emotions.slice();
		const resultData = results.slice();
		const labelData = labels.slice();
		emotion_cooperation_result_by_day_rating.value = ratingData;
		emotion_cooperation_result_by_day_emotion.value = emotionData;
		emotion_cooperation_result_by_day_result.value = resultData;
		emotion_cooperation_result_by_day_labels.value = labelData;
		emotion_cooperation_result_by_day_data.value = toMixSeries(
			ratingData,
			emotionData,
			resultData
		);
		mixChartOptions.value = {
			...mixChartOptions.value,
			labels: labelData,
			plotOptions: {
				bar: {
					horizontal: false,
					columnWidth: done ? mixColumnWidth(labelData.length) : '30%',
					borderRadius: 4,
				},
			},
		};
	};

	const step = () => {
		if (token !== mixFeedToken) return;
		const end = Math.min(list.length, index + chunk);
		for (; index < end; index += 1) {
			const item = list[index] || {};
			emotions.push(+(+item.emotion_scale / 5) * 100 || null);
			ratings.push(+(+item.cooperation_scale / 5) * 100 || null);
			results.push(+item.complete_percent || null);
			labels.push(_GET_DATE_2(item.date, true));
		}
		const done = index >= list.length;
		paint(done);
		if (!done) {
			mixChartFeeding.value = true;
			scheduleMixFeed(step);
			return;
		}
		mixChartFeeding.value = false;
	};

	if (!list.length) {
		paint(true);
		return;
	}
	step();
};
const radarChartOptions = ref({
	chart: {
		id: 'radarChart',
		type: 'radar',
	},
	title: {
		show: false,
	},
	stroke: {
		width: 0,
	},
	fill: {
		opacity: 0.2,
		colors: ['#65A30D'],
	},
	markers: {
		size: 5,
		hover: {
			size: 10,
		},
		colors: ['#65A30D'],
	},
	dataLabels: {
		enabled: true,
		formatter: function (val) {
			return val + '%';
		},
		style: {
			fontFamily: 'Gilrory',
			fontSize: '20px',
			fontWeight: 'bold',
			colors: ['#344256'],
		},
		background: {
			enabled: false,
		},
	},
	yaxis: {
		show: false,
		logBase: 100,
		tickAmount: 10,
		min: 0,
		max: 100,
		labels: {
			show: false,
		},
	},
	xaxis: {
		categories: [],
		labels: {
			show: true,
			style: {
				fontFamily: 'Gilrory',
				fontSize: '14px',
				fontWeight: 600,
				colors: Array(1000).fill('#344256'),
			},
			formatter: function (val, index) {
				return index?.dataPointIndex + 1;
			},
		},
	},
	tooltip: {
		x: {
			show: true,
			formatter: function (val, object) {
				return `${object?.dataPointIndex + 1}: ${val}`;
			},
			title: {
				formatter: (seriesName) => seriesName,
			},
		},
		y: {
			show: true,
			formatter: function (val) {
				return val + '%';
			},
			title: {
				title: {
					formatter: (seriesName) => seriesName + ':',
				},
			},
		},
	},
});
const series = ref([]);
const series3 = ref([]);
const radarData = ref([]);
const categories = ref([]);
const recommend = ref('');
const student = ref(null);
const range = ref(15);
const note = ref('');
const assessment_results = ref([]);
const editMode = ref(false);
const editGoal = ref(false);
const skills = ref([]);
const skillsWithAge = ref([]);
const goalIndex = ref(null);
const unitType = ref(['tháng tuổi', 'tuổi']);
const store = useStore();
const goalId = ref(null);
const isShow = ref(false);

const formatDate = (dateString, reverse) => {
	const date = new Date(dateString);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	let formattedDate;
	if (reverse) formattedDate = `${day}/${month}/${year}`;
	else formattedDate = `${year}-${month}-${day}`;
	return formattedDate;
};

const fetchPeriodsReport = async () => {
	loading.value = true;
	try {
		emotion_cooperation_result_by_day.value = [];
		emotion_cooperation_result_by_day_emotion.value = [];
		emotion_cooperation_result_by_day_rating.value = [];
		emotion_cooperation_result_by_day_result.value = [];
		emotion_cooperation_result_by_day_labels.value = [];
		emotion_cooperation_result_by_day_data.value = [
			{
				name: 'Kết quả buổi học',
				type: 'line',
				data: [0, 0, 0, 0, 0],
			},
			{
				name: 'Sự tham gia',
				type: 'column',
				data: [0, 0, 0, 0, 0],
			},
			{
				name: 'Cảm xúc',
				type: 'column',
				data: [0, 0, 0, 0, 0],
			},
		];
		const res = await $api.monthlyReport.periodReports(
			route.params.id,
			route.query.start_date,
			route.query.end_date
		);
		if (res) {
			data.value = res.data;
			statistics.value = res.data.data;
			note.value = res.data?.note;
			recommend.value = res.data?.recomended_for_parent;
			student.value = res.data.student_and_assessment_conclusion;
			range.value = getAge(student.value?.student?.age) + 2;

			if (
				statistics.value?.goal_count === 0 &&
				data.value?.stats?.attendance === 0
			) {
				empty.value = true;
				loading.value = false;
				return;
			}
			emotion_cooperation_result_by_day.value =
				res.data.stats?.emotion_cooperation_result_by_day;
			assessment_results.value = res.data.skills;
			skills.value = res.data?.skill_notes_and_ages.map(
				(item, index) => ({
					...item,
					...assessment_results.value[index],
				})
			);
			skillsWithAge.value = skills.value.filter((i) => i.age);
			let a = [];
			let count = 0;
			result.value = res.data.stats?.result;
			result.value.forEach((item) => {
				if (
					item.result === 'Từ chối' ||
					item.result === 'Không có cơ hội' ||
					item.result === 'Không hợp tác'
				) {
					count += item.count;
				} else {
					a.push(item);
				}
			});
			a.push({
				result: 'X',
				count: count,
			});
			series.value = a.map((item) => {
				return item.count;
			});
			let series3Total = 0;
			res.data.stats?.emotion_scale.forEach((item) => {
				return (series3Total += item.count);
			});
			series3.value = res.data.stats?.emotion_scale.map((item) => {
				const r = (item.count * 100) / series3Total;
				return Math.round(r * 10) / 10 || 0;
			});
			feedMixChart(emotion_cooperation_result_by_day.value);
			if (data.value.skills.length) {
				radarData.value = [
					{
						name: 'Kết quả',
						data: data.value.skills.map((skill) => {
							return skill?.skill?.skill_percent;
						}),
					},
				];
				radarChartOptions.value.xaxis.categories =
					data.value.skills.map((skill) => {
						return skill?.skill?.name;
					});
				categories.value = data.value.skills.map((skill) => {
					return skill?.skill?.name;
				});
			}
		}
	} catch (e) {
		console.log(e);
	} finally {
		loading.value = false;
	}
};
function formatValue(value) {
	if (value < 72) {
		return value + 'th';
	} else {
		const years = Math.floor(value / 12);
		return years + 'T';
	}
}

const formattedValues = computed(() => {
	const unique = new Set();
	const result = [];
	for (let i = 1; i <= range.value; i++) {
		const value = i * 6 - 6;
		const formatted = formatValue(value);
		if (!unique.has(formatted)) {
			unique.add(formatted);
			result.push(formatted);
		}
	}
	return result;
});

function getAge(data) {
	if (data) {
		data = data.toLowerCase().trim();
		if (data.includes('tuổi') && data.includes('tháng')) {
			const ageMatch = data.match(/(\d+)\s*tuổi\s*(\d*)\s*tháng?/);
			if (ageMatch) {
				const years = parseInt(ageMatch[1], 10);
				const months = parseInt(ageMatch[2] || '0', 10);
				return years * 2 + (months >= 6 ? 1 : 0);
			}
		} else if (data.includes('tuổi')) {
			const match = data.match(/(\d+)\s*tuổi/);
			if (match) {
				const years = parseInt(match[1], 10);
				return years * 2;
			}
		} else if (data.includes('tháng') && !data.includes('tuổi')) return 5;
	} else return 15;
}

function parseAgeToMonths(ageString) {
	const yearMatch = ageString.match(/(\d+)\s*tuổi/);
	const monthMatch = ageString.match(/(\d+)\s*tháng/);
	const years = yearMatch ? parseInt(yearMatch[1]) : 0;
	const months = monthMatch ? parseInt(monthMatch[1]) : 0;
	return years * 12 + months;
}

function getAgeWidth(ageInMonths) {
	const basePx = 64;

	if (ageInMonths <= 72) {
		return (ageInMonths / 6) * basePx + 'px';
	} else {
		const first72 = (72 / 6) * basePx;
		const extraMonths = ageInMonths - 72;
		const extraYears = extraMonths / 12;
		return first72 + extraYears * basePx + 'px';
	}
}
function edit(data) {
	if (editGoal.value) editReport();
	goalIndex.value = data;
	editGoal.value = true;
}
function save() {
	goalIndex.value = null;
	editGoal.value = false;
	editReport();
}

function saveRecommend() {
	editMode.value = false;
	editReport();
}

function openGoalDetail(id) {
	goalId.value = id;
	isShow.value = true;
}

watch(
	skills,
	(value) => {
		value.forEach((v) => {
			if (v.age && !v.unit) {
				v.unit = 'tháng tuổi';
			}
		});
		skillsWithAge.value = value.filter((i) => i.age);
	},
	{ deep: true }
);

const editReport = async () => {
	try {
		let skill =
			skills.value.length > 0 ? skills.value : assessment_results.value;
		let listHideId = [];
		listHideId = skill
			.flatMap((skill) => skill.goals)
			.filter((goal) => goal.is_hidden)
			.filter((goal) => goal.is_hidden)
			.map((goal) => goal.goal_id);
		if (skills.value.length > 0) {
			skills.value.forEach((s) => {
				if (s.unit === 'tuổi' && s.age < 6) {
					s.age = s.age * 12;
					s.unit = 'tháng tuổi';
				}
			});
		}
		await $api.student.editReport(
			Number(route.params.id),
			Number(data.value.period_report_id),
			{
				skill_notes_and_ages: skills.value.map(
					({ goals, skill, ...rest }) => rest
				),
				hidden_goal_ids: listHideId,
				recomended_for_parent: recommend.value,
				note: note.value,
			}
		);

		await store.dispatch('setAlertMessage', {
			message: 'Chỉnh sửa kết quả đánh giá thành công',
		});

		emit('updateResult');
	} catch (e) {
		store.dispatch('setAlertMessage', {
			message:
				e?.response?.data?.errors?.join('<br/>') ||
				'Đã có lỗi xảy ra. Vui lòng thử lại',
			type: 'danger',
		});
	}
};
onMounted(() => {
	fetchPeriodsReport();
});
onBeforeUnmount(cancelMixFeed);
</script>
<template>
	<div :class="isShow ? 'side-over--active' : ''">
		<div
			v-if="isShow"
			class="fixed h-screen w-screen top-0 left-0 bg-black opacity-20 z-30"
			@click="isShow = false"
		></div>
		<GoalDetail
			@close="isShow = false"
			:goalId="goalId"
			:sideOverShow="isShow"
		/>
	</div>
	<div v-if="loading" class="mt-10 flex items-center justify-center h-28">
		<Loading />
	</div>
	<div v-else>
		<div class="relative mt-12">
			<div aria-hidden="true" class="absolute inset-0 flex items-center">
				<div class="w-full border-t border-neutral-300" />
			</div>
			<div class="relative flex items-center justify-center">
				<div class="px-2.5 py-1 bg-body text-neutral-600 font-semibold">
					Thống kê chung
				</div>
			</div>
		</div>
		<div class="mt-10">
			<div class="p-6 bg-white shadow-sm rounded-2xl">
				<div class="w-full flex items-center justify-between mb-6">
					<div class="font-semibold text-neutral-600">
						Thống kê chung
					</div>
					<div></div>
				</div>
				<div class="flex flex-nowrap w-full md-down:flex-col">
					<div
						class="p-6 border rounded-l-lg border-neutral-200 w-full md-down:rounded-t md-down:rounded-b-none"
					>
						<div
							class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
						>
							<img
								alt=""
								class="mr-2"
								height="32"
								src="@/assets/images/icons/abc.png"
								style="height: 32px"
								width="32"
							/>
							{{
								Number(statistics?.skill_count || 0) +
								'/' +
								Number(data?.stats?.total_skill_count || 0)
							}}
						</div>
						<div class="flex flex-nowrap items-center">
							<span class="text-neutral-500 text-sm"
								>Lĩnh vực đã học
							</span>
						</div>
					</div>
					<div
						class="p-6 border-t border-b border-neutral-200 w-full md-down:border-l md-down:border-r md-down:border-b-0"
					>
						<div
							class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
						>
							<img
								alt=""
								class="mr-2"
								height="32"
								src="@/assets/images/icons/light.png"
								style="height: 32px"
								width="32"
							/>
							{{
								Number(statistics?.goal_count || 0) +
								'/' +
								Number(data?.stats?.total_goal_count || 0)
							}}
						</div>
						<div class="flex flex-nowrap items-center">
							<span class="text-neutral-500 text-sm"
								>Mục tiêu đã học
							</span>
						</div>
					</div>
					<div
						class="p-6 border rounded-r-lg border-neutral-200 w-full md-down:rounded-b md-down:rounded-t-none"
					>
						<div
							class="text-33 font-bold text-neutral-600 mb-6 flex flex-nowrap items-center"
						>
							<img
								alt=""
								class="mr-2"
								height="32"
								src="@/assets/images/icons/light.png"
								style="height: 32px"
								width="32"
							/>
							{{ statistics?.completed || 0 }}
						</div>
						<div class="flex flex-nowrap items-center">
							<span class="text-neutral-500 text-sm"
								>Mục tiêu đã hoàn thành
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="mt-6" v-if="note || recommend">
			<div class="bg-white shadow-sm rounded-2xl">
				<div
					class="w-full flex items-center justify-between p-6"
					:class="{ 'border-bot': note || recommend }"
				>
					<div class="font-semibold text-neutral-600">
						Nhận xét chung về quá trình can thiệp của trẻ
					</div>
					<div v-if="editMode">
						<button
							class="flex items-center gap-2 luca-button"
							@click="saveRecommend"
						>
							<img
								class="w-4 h-4"
								src="@/assets/images/icons/save.png"
								alt=""
							/>
							<div>Lưu chỉnh sửa</div>
						</button>
					</div>
					<div v-else>
						<div
							class="flex items-center gap-2 cursor-pointer"
							@click="editMode = true"
						>
							<img
								class="w-4 h-4"
								src="@/assets/images/icons/edit-fill.png"
								alt=""
							/>
							<div class="text-yellow-700 font-semibold">
								Chỉnh sửa
							</div>
						</div>
					</div>
				</div>
				<div class="p-6">
					<div v-if="editMode">
						<div
							class="text-gray-400 font-semibold mb-1 uppercase text-xs"
						>
							NHẬN XÉT
						</div>
						<textarea class="mb-4" rows="6" v-model="note"></textarea>
						<div
							class="text-gray-400 font-semibold mb-1 uppercase text-xs"
						>
							Gợi ý cho phụ huynh
						</div>
						<textarea rows="6" v-model="recommend"></textarea>
					</div>
					<div v-else>
						<div v-if="note" class="mb-4">
							<div
								class="text-gray-400 font-semibold mb-1 uppercase text-xs"
							>
								NHẬN XÉT
							</div>
							<div
								class="text-gray-700"
								v-html="note.replaceAll('\n', '<br />')"
							></div>
						</div>
						<div v-if="recommend">
							<div
								class="text-gray-400 font-semibold mb-1 uppercase text-xs"
							>
								Gợi ý cho phụ huynh
							</div>
							<div
								class="text-gray-700"
								v-html="recommend.replaceAll('\n', '<br />')"
							></div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="mt-6" v-if="skillsWithAge.length > 0">
			<div class="bg-white shadow border-sm rounded-lg">
				<div class="border-bot">
					<div class="text-neutral-500 font-semibold border-b p-6">
						Biểu đồ mối tương quan tuổi phát triển và tuổi thực của
						trẻ qua các lĩnh vực đã học
					</div>
					<div
						class="font-semibold text-gray-700 whitespace-nowrap px-6 pt-6 mb-2"
					>
						Lĩnh vực
					</div>
					<div
						class="relative px-6 pb-6 flex items-center text-neutral-500 gap-4 overflow-x-auto"
					>
						<div class="h-full pb-9">
							<div
								:style="{
									height: 45 * skillsWithAge.length + 'px',
								}"
								class="flex flex-col items-start justify-between"
							>
								<div
									v-for="i in skillsWithAge"
									:key="i.id"
									class="font-semibold text-gray-400 whitespace-nowrap"
								>
									{{ i.name }}
								</div>
							</div>
						</div>
						<div class="w-max flex flex-nowrap pt-6 relative">
							<div
								class="absolute l-0 w-full flex flex-col items-start justify-between"
								:style="{
									height: 45 * skillsWithAge.length + 'px',
								}"
							>
								<div
									v-for="i in skillsWithAge"
									:key="i.id"
									class="flex items-center gap-2 w-full"
								>
									<div
										:style="{
											width: getAgeWidth(i.age),
										}"
										class="percent"
									></div>
									<div
										class="text-gray-500 font-semibold text-sm"
									>
										{{ i.age }}
									</div>
								</div>
							</div>
							<div
								v-for="i in formattedValues"
								:key="i"
								class="flex-1 w-16"
							>
								<div
									class="border border-t-0 border-b-0 border-l border-neutral-200"
									:class="{
										'border-r-0':
											i !== formattedValues.length - 1,
									}"
									:style="{
										height:
											45 * skillsWithAge.length + 'px',
									}"
								></div>
								<div
									class="text-neutral-500 text-sm mt-8 font-semibold float-left -ml-2"
								>
									{{ i }}
								</div>
							</div>
							<div
								class="absolute text-gray-500 font-semibold whitespace-nowrap"
								:style="{
									left: getAgeWidth(
										parseAgeToMonths(student?.student?.age)
									),
									'margin-left': '-25px',
									top: '-5px',
								}"
							>
								{{ student?.student?.age }}
							</div>
							<div
								class="absolute student-age"
								:style="{
									left: getAgeWidth(
										parseAgeToMonths(student?.student?.age)
									),
									height: 45 * skillsWithAge.length + 'px',
								}"
							></div>
						</div>
						<div
							class="flex items-end font-semibold text-gray-700 whitespace-nowrap"
							:style="{
								height: 45 * skillsWithAge.length + 81 + 'px',
							}"
						>
							Tuổi phát triển
						</div>
					</div>
					<div class="text-gray-700 flex item-center gap-10 p-6">
						<div class="flex items-center gap-2">
							<div class="r-age w-4 h-4 rounded-lg"></div>
							<div class="text-sm">Tuổi thực</div>
						</div>
						<div class="flex items-center gap-2">
							<div class="d-age w-4 h-4 rounded-lg"></div>
							<div class="text-sm">Tuổi phát triển</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div
			v-if="series3.length > 0"
			class="mt-6 flex items-stretch gap-6 xl-down:flex-col"
		>
			<div
				class="bg-white shadow-sm rounded-2xl overflow-hidden w-1/3 xl-down:w-full"
			>
				<div class="font-semibold text-neutral-500 p-6">
					Biều đồ cảm xúc
				</div>
				<div
					class="border-t border-orange-50 flex justify-center items-center p-6 flex-col"
				>
					<div
						class="relative w-full md-down:w-full flex items-center justify-center"
					>
						<apexchart
							:options="chartOptions3"
							:series="series3"
							height="220"
							type="donut"
						></apexchart>
						<div
							class="text-center absolute top-1/2 left-1/2 transform"
							style="transform: translate(-50%, -50%)"
						>
							<div class="text-neutral-500">Cảm xúc</div>
						</div>
					</div>
					<div class="w-full">
						<div
							v-if="series3[0]"
							class="flex flex-nowrap items-center justify-between w-full"
						>
							<div class="flex flex-nowrap items-center">
								<img
									alt=""
									height="24"
									src="@/assets/images/emoji/happy.png"
									width="24"
								/>
								<div
									class="h-2.5 w-2.5 rounded-full ml-2"
									style="background: #9edf93"
								></div>
								<div class="ml-2 text-base text-neutral-500">
									Vui vẻ
								</div>
							</div>
							<div class="text-base text-neutral-600 font-bold">
								{{ series3[0] }}
								%
							</div>
						</div>
						<div
							v-if="series3[1]"
							class="flex flex-nowrap items-center justify-between w-full mt-2.5"
						>
							<div class="flex flex-nowrap items-center">
								<img
									alt=""
									height="24"
									src="@/assets/images/emoji/5565d66a50b44df9ae37a955bdfb3757.png"
									width="24"
								/>
								<div
									class="h-2.5 w-2.5 rounded-full ml-2"
									style="background: #d0db54"
								></div>
								<div class="ml-2 text-base text-neutral-500">
									Bình thường
								</div>
							</div>
							<div class="text-base text-neutral-600 font-bold">
								{{ series3[1] }}
								%
							</div>
						</div>
						<div
							v-if="series3[2] > 0"
							class="flex flex-nowrap items-center justify-between w-full mt-2.5"
						>
							<div class="flex flex-nowrap items-center">
								<img
									alt=""
									height="24"
									src="@/assets/images/emoji/cf1f643bc1244f5e8e37767bc946d607.png"
									width="24"
								/>
								<div
									class="h-2.5 w-2.5 rounded-full ml-2"
									style="background: #ffe68c"
								></div>
								<div class="ml-2 text-base text-neutral-500">
									Khó chịu
								</div>
							</div>
							<div class="text-base text-neutral-600 font-bold">
								{{ series3[2] }}
								%
							</div>
						</div>
						<div
							v-if="series3[3] > 0"
							class="flex flex-nowrap items-center justify-between w-full mt-2.5"
						>
							<div class="flex flex-nowrap items-center">
								<img
									alt=""
									height="24"
									src="@/assets/images/emoji/5ede936757cd4fbdb6247bad22bf454d.png"
									width="24"
								/>
								<div
									class="h-2.5 w-2.5 rounded-full ml-2"
									style="background: #ff9d6d"
								></div>
								<div class="ml-2 text-base text-neutral-500">
									Tức giận
								</div>
							</div>
							<div class="text-base text-neutral-600 font-bold">
								{{ series3[3] }}
								%
							</div>
						</div>
						<div
							v-if="series3[4] > 0"
							class="flex flex-nowrap items-center justify-between w-full mt-2.5"
						>
							<div class="flex flex-nowrap items-center">
								<img
									alt=""
									height="24"
									src="@/assets/images/emoji/0b356bb507d64b9489e2fbced131f795.png"
									width="24"
								/>
								<div
									class="h-2.5 w-2.5 rounded-full ml-2"
									style="background: #ff6967"
								></div>
								<div class="ml-2 text-base text-neutral-500">
									Bùng nổ
								</div>
							</div>
							<div class="text-base text-neutral-600 font-bold">
								{{ series3[4] }}
								%
							</div>
						</div>
					</div>
				</div>
			</div>
			<div
				class="bg-white shadow-sm rounded-2xl overflow-hidden w-1/3 xl-down:w-full xl-down:mt-6"
			>
				<div class="font-semibold text-neutral-500 p-6">
					Biểu đồ kết quả các mục tiêu
				</div>
				<div
					class="flex flex-nowrap p-6 border-t border-orange-50 items-center justify-center flex-col"
				>
					<div class="relative w-fit md-down:w-full">
						<apexchart
							:options="chartOptions"
							:series="series"
							height="220"
							type="donut"
						></apexchart>
						<div
							class="text-center absolute top-1/2 left-1/2 transform"
							style="transform: translate(-50%, -50%)"
						>
							<div class="text-4xl font-bold text-neutral-600">
								{{ statistics?.goal_count }}
							</div>
							<div class="text-neutral-500">Mục tiêu</div>
						</div>
					</div>
					<div
						class="flex flex-col w-full justify-center md-down:mt-4"
					>
						<div
							v-if="result[0]?.count > 0"
							class="flex flex-nowrap items-center mb-4 justify-between"
						>
							<div
								class="text-sm font-semibold text-neutral-500 w-14 blue"
							>
								{{ result[0]?.count }}
								M.T
							</div>
							<span
								class="flex items-center justify-center py-0.5 rounded-full text-sm font-medium text-white whitespace-nowrap w-32 shadow-input"
								style="background: #2aa797"
							>
								{{ result[0]?.result }}
							</span>
						</div>
						<div
							v-if="result[1]?.count > 0"
							class="flex flex-nowrap items-center mb-4 justify-between"
						>
							<div
								class="text-sm font-semibold text-neutral-500 w-14"
							>
								{{ result[1]?.count }}
								M.T
							</div>
							<span
								class="flex items-center justify-center py-0.5 rounded-full text-sm font-medium text-white w-32 whitespace-nowrap shadow-input"
								style="background: #65a30d"
							>
								{{ result[1]?.result }}
							</span>
						</div>
						<div
							v-if="result[2]?.count > 0"
							class="flex flex-nowrap items-center mb-4 justify-between"
						>
							<div
								class="text-sm font-semibold text-neutral-500 w-14"
							>
								{{ result[2]?.count }}
								M.T
							</div>
							<span
								class="flex items-center justify-center py-0.5 rounded-full text-sm font-medium text-white w-32 whitespace-nowrap shadow-input"
								style="background: #f59e0b"
							>
								{{ result[2]?.result }}
							</span>
						</div>
						<div
							v-if="result[3]?.count > 0"
							class="flex flex-nowrap items-center mb-4 justify-between"
						>
							<div
								class="text-sm font-semibold text-neutral-500 w-14 blue"
							>
								{{ result[3]?.count }}
								M.T
							</div>
							<span
								class="flex items-center justify-center px-3 py-0.5 rounded-full text-sm font-medium text-white w-32 whitespace-nowrap shadow-input"
								style="background: #f87171"
							>
								{{ result[3]?.result }}
							</span>
						</div>
						<div
							v-if="
								result[4]?.count > 0 ||
								result[5]?.count > 0 ||
								result[6]?.count > 0
							"
							class="flex flex-nowrap items-center mb-4 justify-between"
						>
							<div
								class="text-sm font-semibold text-neutral-500 w-14 blue"
							>
								{{
									result[4]?.count +
									result[5]?.count +
									result[6]?.count
								}}
								M.T
							</div>
							<span
								class="flex items-center justify-center px-3 py-0.5 rounded-full text-sm font-medium text-white w-32 whitespace-nowrap shadow-input"
								style="background: #9ca3af"
							>
								X
							</span>
						</div>
					</div>
				</div>
			</div>
			<div
				class="bg-white shadow-sm rounded-2xl overflow-hidden w-1/3 xl-down:w-full xl-down:mt-6"
			>
				<div class="font-semibold text-neutral-500 p-6">Chuyên cần</div>
				<div
					class="border-t border-orange-50 p-6 flex items-center justify-center flex-col"
				>
					<div
						class="text-neutral-600 font-semibold mb-5"
						style="font-size: 72px"
					>
						{{ data?.stats?.attendance }}
					</div>
					<div class="text-neutral-500 mb-5 text-center">
						Số ngày học
					</div>
				</div>
			</div>
		</div>
		<div class="mt-6">
			<div
				class="bg-white shadow-sm rounded-2xl overflow-hidden mb-10 pb-5"
			>
				<div class="font-semibold text-neutral-500 p-6">
					Mối tương quan giữa kết quả, cảm xúc và sự tham gia của trẻ
					từ {{ formatDate(startDate, true) }} tới
					{{ formatDate(endDate, true) }}
				</div>
				<p v-if="mixChartFeeding" class="px-6 pb-2 text-sm text-neutral-400">
					Đang vẽ thêm dữ liệu. Bạn vẫn có thể lướt trang.
				</p>
				<div
					class="p-6 border-t border-orange-50 md-down:px-0 overflow-x-auto w-full"
				>
					<apexchart
						ref="mixChart"
						:options="mixChartOptions"
						:series="emotion_cooperation_result_by_day_data"
						:style="{
							minWidth: `${
								emotion_cooperation_result_by_day_emotion?.length *
								150
							}px`,
						}"
						height="600"
						style="width: 100%"
						width="100%"
					></apexchart>
				</div>
			</div>
		</div>
		<div
			v-if="radarData.length && radarData[0]?.data.length >= 3"
			class="mt-12"
		>
			<div
				class="bg-white shadow-sm overflow-hidden rounded-2xl mb-10 pb-5"
			>
				<div class="font-semibold text-neutral-500 p-6">
					Biểu đồ mối tương quan giữa kết quả các lĩnh vực từ
					{{ formatDate(startDate, true) }} tới
					{{ formatDate(endDate, true) }}
				</div>
				<div
					class="p-6 border-t border-orange-50 md-down:px-0 overflow-x-auto w-full"
				>
					<apexchart
						:options="radarChartOptions"
						:series="radarData"
						height="700"
						type="radar"
					></apexchart>
					<div
						class="py-4 flex flex-wrap gap-6 justify-center items-center"
					>
						<div
							v-for="(c, index) in categories"
							:key="c"
							class="flex items-center flex-nowrap gap-2"
						>
							<div class="font-bold text-lf text-neutral-700">
								{{ index + 1 }}: {{ c }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="relative mt-10">
			<div aria-hidden="true" class="absolute inset-0 flex items-center">
				<div class="w-full border-t border-neutral-300" />
			</div>
			<div class="relative flex items-center justify-center">
				<div class="px-2.5 py-1 bg-body text-neutral-600 font-semibold">
					Kết quả mục tiêu đã học
				</div>
			</div>
		</div>
		<div
			v-for="(s, index) in skills.length > 0
				? skills
				: assessment_results"
			:key="index"
			class="mt-10"
			:class="{
				goalblock: skills.length > 0,
				'shadow-sm': skills.length > 0,
			}"
		>
			<div class="flex items-center justify-between mb-4">
				<div class="flex items-center">
					<div class="font-semibold text-neutral-700">
						{{ index + 1 }}. {{ s.skill?.name }}
					</div>
					<div class="flex items-center gap-2" v-if="s.age">
						<div>:</div>
						<div class="font-semibold text-green-500">
							{{ s.age }} {{ s.unit }}
						</div>
					</div>
				</div>
				<div v-if="editGoal && goalIndex === index">
					<button
						class="flex items-center gap-2 luca-button"
						@click="save"
					>
						<img
							class="w-4 h-4"
							src="@/assets/images/icons/save.png"
							alt=""
						/>
						<div>Lưu chỉnh sửa</div>
					</button>
				</div>
				<div
					v-else
					class="flex items-center gap-2 cursor-pointer"
					@click="edit(index)"
				>
					<img
						class="w-4 h-4"
						src="@/assets/images/icons/edit-fill.png"
						alt=""
					/>
					<div class="text-yellow-700 font-semibold">Chỉnh sửa</div>
				</div>
			</div>
			<div v-if="skills.length > 0">
				<div
					class="text-xs font-semibold uppercase mb-2 text-gray-400"
					v-if="s.age || (editGoal && goalIndex === index)"
				>
					Tuổi phát triẻn
				</div>
				<div class="mb-4">
					<div
						v-if="editGoal && goalIndex === index"
						class="flex items-center gap-2"
					>
						<div class="input-group" style="width: 100px">
							<input type="number" v-model="s.age" />
						</div>
						<Multiselect
							style="width: 200px"
							:options="unitType"
							v-model="s.unit"
							:allow-empty="false"
							placeholder="Chọn loại tuổi"
							deselect-label=""
							selectLabel=""
						>
						</Multiselect>
					</div>
					<div v-else class="text-gray-700">
						<span v-if="s.age">{{ s.age }} {{ s.unit }}</span>
					</div>
				</div>
				<div class="mb-4">
					<div
						class="text-xs font-semibold uppercase mb-2 text-gray-400"
						v-if="s.note || (editGoal && goalIndex === index)"
					>
						Nhận xét chung
					</div>
					<textarea
						v-if="editGoal && goalIndex === index"
						rows="6"
						v-model="s.note"
					>
					</textarea>
					<div v-else class="text-gray-700" v-html="s.note"></div>
				</div>
				<div class="mb-4">
					<div
						class="text-xs font-semibold uppercase mb-2 text-gray-400"
						v-if="
							s.recomended_for_parent ||
							(editGoal && goalIndex === index)
						"
					>
						Gợi ý cho phụ huynh
					</div>
					<textarea
						v-if="editGoal && goalIndex === index"
						rows="6"
						v-model="s.recomended_for_parent"
					>
					</textarea>
					<div
						v-else
						class="text-gray-700"
						v-html="s.recomended_for_parent"
					></div>
				</div>
				<div class="text-xs font-semibold uppercase mb-2 text-gray-400">
					Các mục tiêu đã học
				</div>
			</div>
			<div
				class="shadow-sm sm:rounded-2xl overflow-hidden md-down:hidden"
			>
				<div class="__record__table overflow-x-auto scroll-bar">
					<div
						class="overflow-hidden sm:rounded-t-2xl w-fit min-w-full"
					>
						<table class="min-w-full divide-y text-neutral-200">
							<thead class="bg-neutral-100 h-16">
								<tr>
									<th
										class="px-4 py-3 text-center text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap w-16"
										scope="col"
									>
										STT
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
									>
										Mục Tiêu
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 uppercase tracking-wider whitespace-nowrap"
										scope="col"
										style="width: 80px"
									>
										Kết quả
									</th>
									<th
										class="px-6 py-3 text-left text-xs font-semibold text-neutral-600 tracking-wider whitespace-nowrap"
										scope="col"
										style="width: 140px"
										v-if="editGoal && goalIndex === index"
									>
										<div class="flex items-center gap-1">
											<div class="uppercase">
												Hiển thị MT
											</div>
											<Popper
												placement="top"
												hover
												:arrow="true"
											>
												<img
													class="cursor-pointer"
													src="@/assets/images/icons/info-circle-fill.png"
													alt=""
												/>

												<template #content>
													<div
														style="max-width: 276px"
														class="text-lg whitespace-normal text-neutral-600 font-normal"
													>
														Khi tắt, mục tiêu sẽ
														không được hiển thị trên
														báo cáo can thiệp định
														kỳ của trẻ.
													</div>
												</template>
											</Popper>
										</div>
									</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(i, gIndex) in s.goals"
									:key="gIndex"
									:class="[
										gIndex % 2 === 0
											? 'bg-white'
											: 'bg-neutral-50',
										i.is_hidden && goalIndex !== index
											? 'opacity-30'
											: '',
									]"
									class="cursor-pointer text-neutral-700 hover:bg-orange-50"
									@click="openGoalDetail(i.goal_id || i.id)"
								>
									<td
										class="p-4 whitespace-nowrap text-neutral-700 text-center"
									>
										{{ gIndex + 1 }}
									</td>
									<td
										class="px-6 py-4 whitespace-nowrap text-neutral-700"
									>
										<div class="flex items-center gap-2">
											<div
												class="whitespace-normal"
												v-html="i.name || i.goal_name"
											></div>
											<TooltipGoalDetail
												:goal-id="i.goal_id || i.id"
												:student-id="route.params.id"
											/>
										</div>
										<div
											class="flex gap-2 flex-wrap"
											style="max-width: 360px"
										>
											<div
												class="mt-1 py-0.5 px-2.5 rounded-full bg-neutral-200 text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
												style="font-size: 10px"
												v-if="i?.source"
											>
												{{ i?.source }}
											</div>
											<div
												class="mt-1 py-0.5 px-2.5 rounded-full bg-neutral-200 text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
												style="font-size: 10px"
												v-else
											>
												{{
													$filters.sourceMap(
														i?.state,
														i.editable
													)
												}}
											</div>
											<template
												v-if="i?.assistance_levels"
											>
												<div
													v-for="ass in i?.assistance_levels"
													:key="ass"
													class="flex gap-2"
												>
													<div
														class="mt-1 py-0.5 px-2.5 rounded-full bg-pink-100 whitespace-nowrap text-neutral-700 h-fit w-fit font-semibold shadow-input"
														style="font-size: 10px"
													>
														{{ ass }}
													</div>
												</div>
											</template>
											<div
												class="mt-1 py-0.5 px-2.5 rounded-full bg-yellow-tag text-neutral-700 h-fit w-fit font-semibold shadow-input whitespace-nowrap"
												style="font-size: 10px"
												v-if="i?.age"
											>
												{{ i?.age }}
											</div>
										</div>
										<div
											class="flex items-center gap-2 mt-2 text-neutral-400 text-xs font-semibold"
										>
											<div v-if="i?.has_media">
												<img
													alt=""
													src="@/assets/images/icons/has_media.png"
												/>
											</div>
											<div
												v-if="
													i.number_of_assessment > 0
												"
											>
												ĐÃ HỌC
												{{ i.number_of_assessment }}
												NGÀY
											</div>
											<div v-if="i.start_date">|</div>
											<div v-if="i.start_date">
												{{
													$filters.formatDate(
														i.start_date
													)
												}}
											</div>
											<div v-if="i.completed_at">-</div>
											<div v-if="i.completed_at">
												{{
													$filters.formatDate(
														i.completed_at
													)
												}}
											</div>
											<div v-if="i?.completed_at">
												<img
													alt=""
													src="@/assets/images/icons/has_taught.png"
												/>
											</div>
										</div>
										<div
											v-if="i.parent?.sub_goal_count > 0"
											class="goal-block flex gap-3 mt-2"
										>
											<img
												class="w-3 h-3 mt-1"
												src="@/assets/images/icons/goal-arrow.png"
												alt=""
											/>
											<div>
												<div
													class="text-neutral-700 whitespace-normal"
													v-html="i.parent?.name"
												></div>
												<div
													class="text-neutral-400 text-xs font-semibold"
												>
													{{
														i.parent?.sub_goal_count
													}}
													MỤC TIÊU
												</div>
											</div>
										</div>
									</td>
									<td
										class="px-6 py-4 whitespace-nowrap text-neutral-700"
									>
										<div>
											<div
												v-if="i?.complete_percent"
												:style="{
													background:
														$filters.tagResult(
															i?.complete_percent
														)?.background,
													color: $filters.tagResult(
														i?.complete_percent
													)?.color,
												}"
												class="py-0.5 px-2.5 rounded-full text-neutral-700 h-fit w-fit font-semibold text-sm shadow-input"
											>
												Đạt
												{{ i?.complete_percent || 0 }}%
											</div>
										</div>
									</td>
									<td
										v-if="editGoal && goalIndex === index"
										class="px-6 py-4 whitespace-nowrap text-neutral-700"
									>
										<div
											class="flex items-center justify-center"
											@click.stop="
												i.is_hidden = !i.is_hidden
											"
										>
											<img
												v-if="!i.is_hidden"
												src="@/assets/images/icons/eye-yellow.png"
											/>
											<img
												v-else
												src="@/assets/images/icons/eye-slash-fill.png"
											/>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
@import '../../../../../assets/styles/responsive';

.is-MT-show {
	width: 100vw;
	transform: translateX(0);
	transition: all 0.4s ease;

	@include media-breakpoint-up(lg) {
		width: 400px;
	}
}

.is-MT-hide {
	transform: translateX(450px);
	transition: all 0.4s ease;
}

.transition {
	transition: all 0.4s ease;
}

.content-report-is-show {
	@include media-breakpoint-up(lg) {
		padding-right: 448px;
	}
}

.content-report-is-hide {
	@include media-breakpoint-up(lg) {
		padding-right: 40px;
	}
}
.border-bot {
	border-bottom: 2px solid #faf5f1;
}
.percent {
	height: 24px;
	border-radius: 0 99px 99px 0;
	background: #f9a8d4;
}
.student-age {
	border: 3px dashed #86a8d0;
}
.r-age {
	background: #6390c3;
}
.d-age {
	background: #f9a8d4;
}
.student-age::before,
.student-age::after {
	content: '';
	position: absolute;
	left: -7.5px;
	width: 15px;
	height: 15px;
	background-color: #6390c3;
	border-radius: 50%;
}

.student-age::before {
	top: -10px;
}

.student-age::after {
	bottom: -10px;
}
.goalblock {
	border-radius: 8px;
	padding: 24px;
	background: #ffffff;
	border: none !important;
}
.opacity-30 {
	opacity: 0.3 !important;
}
</style>
